import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getToken } from 'src/auth';
import { api } from 'src/api/config';
import { setBreadcrumbs } from 'src/ui';
import { FIConfigModal } from '../components/FIConfigModal';

function FIConfigPageComponent({ token, setBreadcrumbs }) {
  // State for FI list
  const [fis, setFis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('enabled'); // 'all', 'enabled', 'disabled'

  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFI, setSelectedFI] = useState(null);

  // Fetch FIs
  const fetchFIs = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${api.hostname}/api/v1/pos/fis/`, {
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch FIs: ${response.status}`);
      }

      const data = await response.json();
      setFis(data);
    } catch (err) {
      console.error('Failed to fetch FIs:', err);
      setError(err.message || 'Failed to load ISOs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'SolventPay', href: '/solventpay' },
      { label: 'ISO Config', href: '/solventpay/fi-config' },
    ]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchFIs();
  }, [fetchFIs]);

  // Handle Configure click
  const handleConfigure = (fi) => {
    setSelectedFI(fi);
    setModalOpen(true);
  };

  // Handle modal save
  const handleSave = async (formData) => {
    try {
      const response = await fetch(`${api.hostname}/api/v1/pos/fis/${selectedFI.id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Request failed: ${response.status}`);
      }

      setModalOpen(false);
      fetchFIs(); // Refresh list
    } catch (err) {
      console.error('Failed to save FI config:', err);
      throw err;
    }
  };

  // Filter FIs by search term and status
  const filteredFIs = fis.filter((fi) => {
    // Status filter
    if (statusFilter === 'enabled' && !fi.allows_payments) return false;
    if (statusFilter === 'disabled' && fi.allows_payments) return false;

    // Search filter
    const searchLower = searchTerm.toLowerCase();
    return (
      fi.name?.toLowerCase().includes(searchLower) ||
      fi.dba_name?.toLowerCase().includes(searchLower) ||
      String(fi.id).includes(searchLower)
    );
  });

  // Count for status badges
  const enabledCount = fis.filter((fi) => fi.allows_payments).length;
  const disabledCount = fis.filter((fi) => !fi.allows_payments).length;

  if (loading && fis.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">ISO Configuration</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">
                All ({fis.length})
              </MenuItem>
              <MenuItem value="enabled">
                <Box display="flex" alignItems="center" gap={1}>
                  Enabled
                  <Chip label={enabledCount} size="small" color="success" sx={{ height: 20 }} />
                </Box>
              </MenuItem>
              <MenuItem value="disabled">
                <Box display="flex" alignItems="center" gap={1}>
                  Disabled
                  <Chip label={disabledCount} size="small" color="default" sx={{ height: 20 }} />
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Search ISOs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250 }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchFIs}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ISO Name</TableCell>
              <TableCell>DBA Name</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFIs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  {searchTerm ? 'No ISOs match your search' : 'No ISOs found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredFIs.map((fi) => (
                <TableRow key={fi.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {fi.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {fi.dba_name || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {fi.allows_payments ? (
                      <Chip label="Payments Enabled" color="success" size="small" />
                    ) : (
                      <Chip label="Payments Disabled" color="default" size="small" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleConfigure(fi)}
                    >
                      Configure
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* FI Config Modal */}
      <FIConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        fiData={selectedFI}
      />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  token: getToken(state),
});

const mapDispatchToProps = {
  setBreadcrumbs,
};

export const FIConfigPage = connect(mapStateToProps, mapDispatchToProps)(FIConfigPageComponent);
